import type { Catalogue } from "../data/schema";
import { BodyTable } from "./body-table";
import { orbitStateAt } from "./elements";
import { createKeplerResult, createOrbitState, positionFromState, solveKepler } from "./kepler";
import { equatorialToEclipticUnit } from "./frames";
import { spinQuaternion, tiltPole } from "./rotation";
import { J2000 } from "./time";

const state = createOrbitState();
const kepler = createKeplerResult();

export class SolarSystem {
  readonly table: BodyTable;
  readonly count: number;
  readonly positions: Float64Array;
  readonly rotations: Float32Array;
  /** Eccentric anomaly per body (radians) from the last update, 0 for bodies without an orbit. */
  readonly anomalies: Float64Array;
  /** Julian date of the last update. */
  jd = NaN;
  /** Unit north pole per body in the J2000 ecliptic. */
  readonly poles: Float64Array;
  readonly stats = { keplerNonConverged: 0, nonFinite: 0 };

  constructor(bodies: Catalogue) {
    this.table = BodyTable.from(bodies);
    this.count = this.table.count;
    this.positions = new Float64Array(3 * this.count);
    this.rotations = new Float32Array(4 * this.count);
    this.anomalies = new Float64Array(this.count);
    this.poles = new Float64Array(3 * this.count);
    bodies.forEach((b, k) => {
      const pole = b.physical.pole;
      if (pole) this.poles.set(equatorialToEclipticUnit(pole.ra, pole.dec), 3 * k);
      else tiltPole(b.physical.axialTiltDeg, this.poles, 3 * k);
    });
  }

  update(jd: number): void {
    this.jd = jd;
    const { positions, rotations, anomalies } = this;
    const { bodies, parentIndex } = this.table;
    for (let k = 0; k < this.count; k++) {
      const body = bodies[k];
      const o = 3 * k;
      const p = parentIndex[k];
      if (p < 0 || body.orbit === null) {
        positions[o] = 0;
        positions[o + 1] = 0;
        positions[o + 2] = 0;
      } else {
        orbitStateAt(body.orbit, jd, state);
        solveKepler(state.M, state.e, kepler);
        if (!kepler.converged) this.stats.keplerNonConverged++;
        anomalies[k] = kepler.E;
        positionFromState(state, kepler.E, positions, o);
        const po = 3 * p;
        if (
          Number.isFinite(positions[o]) &&
          Number.isFinite(positions[o + 1]) &&
          Number.isFinite(positions[o + 2])
        ) {
          positions[o] += positions[po];
          positions[o + 1] += positions[po + 1];
          positions[o + 2] += positions[po + 2];
        } else {
          this.stats.nonFinite++;
          positions[o] = positions[po];
          positions[o + 1] = positions[po + 1];
          positions[o + 2] = positions[po + 2];
        }
      }
      spinQuaternion(body.physical.rotationPeriodHours, this.poles, o, jd, J2000, rotations, 4 * k);
    }
  }
}
